import { chatSpendPossible } from './chatSpend';

export const ACCOUNT_READY_MESSAGE = 'Getting your account ready, try again in a moment.';
export const CONFIGURE_KEY_MESSAGE = 'Configure API key to start chatting';
// Under $0.50 the writer may add their own key. $0.50 exactly is still on the allowance.
export const OWN_KEY_SWITCH_USD = 0.5;
export const BETA_ALLOWANCE_ALMOST_MESSAGE = 'Your beta allowance is almost used up. To keep using AI, add your own OpenRouter key in Settings.';
export const BETA_ALLOWANCE_USED_MESSAGE = 'Your beta allowance is used up. To keep using AI, add your own OpenRouter key in Settings.';
export const BETA_ALLOWANCE_QUIET_LINE = 'When your allowance runs out, you can add your own OpenRouter key.';
export const OWN_KEY_SWITCH_GUIDE = 'Get a key at openrouter.ai, paste it here, and AI costs then go to your own OpenRouter account.';
export const BETA_ALLOWANCE_UNAVAILABLE_MESSAGE = "Can't check your allowance right now.";
export const OWN_KEY_IN_USE_MESSAGE = 'Using your own OpenRouter key.';
export const BETA_BLOCKED_MESSAGE = "Chat isn't available on this account right now. We'll let you know what's next.";
export const REPLY_STOPPED_EARLY_MESSAGE = 'That reply stopped early. Please try again.';
export const REPLY_DID_NOT_ARRIVE_MESSAGE = "That reply didn't come through. Please try again.";

const BETA_KEY_STATES = new Set(['pending', 'ready', 'blocked', 'own_needed']);

/** True for an app-provisioned writer, including while a balance read has failed. */
export function isBetaWriter(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.provisioned === true) return true;
  return BETA_KEY_STATES.has(data.key_provisioning);
}

/**
 * Writer-facing beta label. A stale or person-named line falls back so the
 * screen never addresses the writer by a staff name.
 */
export function betaWriterLabel(label, fallback) {
  const text = typeof label === 'string' ? label.trim() : '';
  if (!text || /\bJoel\b/.test(text)) return fallback;
  return text;
}

// While the allowance is still being set up, ask again with backoff, then stop.
const ALLOWANCE_PENDING_DELAYS_MS = [2000, 4000, 8000, 16000];
export const ALLOWANCE_PENDING_MAX_DELAY_MS = 20000;
export const ALLOWANCE_PENDING_STOP_MS = 90000;

export function allowancePendingDelay(attempt) {
  const index = Math.max(0, attempt);
  if (index < ALLOWANCE_PENDING_DELAYS_MS.length) return ALLOWANCE_PENDING_DELAYS_MS[index];
  return ALLOWANCE_PENDING_MAX_DELAY_MS;
}

export function allowancePendingShouldStop(elapsedMs) {
  return elapsedMs >= ALLOWANCE_PENDING_STOP_MS;
}

/**
 * Whole cents, never rounded up. $9.995 left is $9.99, not $10.00.
 * The gauge face, the hover, and the detail dialog all use this.
 */
export function allowanceCents(balance) {
  const amount = Number(balance);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.floor(amount * 100 + 1e-6);
}

export function formatAllowanceAmount(balance) {
  const cents = allowanceCents(balance);
  const dollars = Math.floor(cents / 100);
  const remainder = cents % 100;
  return `$${dollars}.${String(remainder).padStart(2, '0')} left`;
}

export function allowanceLabel(balance) {
  const amount = Number(balance);
  if (!Number.isFinite(amount)) return 'Beta allowance';
  return `Beta allowance: ${formatAllowanceAmount(balance)}`;
}

// A tenth of a cent. Float noise passes; a real cent of disagreement does not.
const USAGE_AGREES = 0.001;

function asMoney(value) {
  if (value == null || value === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

export function formatCents(cents) {
  const whole = Math.max(0, Math.trunc(Number(cents) || 0));
  const dollars = Math.floor(whole / 100);
  const remainder = whole % 100;
  return `$${dollars}.${String(remainder).padStart(2, '0')}`;
}

/**
 * Left and Used for a beta allowance, in whole cents, summing to the limit.
 *
 * Left is the floored remainder (never rounded up). Used is the key's own
 * usage when that usage plus the remainder already equals the limit;
 * otherwise Used is the limit minus what's left. The displayed cents are
 * chosen so the two figures add up to the limit.
 */
export function allowanceBudget({ balance, limit, usage } = {}) {
  const remaining = asMoney(balance);
  const cap = asMoney(limit);
  const spent = asMoney(usage);
  const hasCap = cap != null && cap > 0;

  let leftAmount = 0;
  if (remaining != null) {
    leftAmount = remaining;
  } else if (hasCap && spent != null) {
    leftAmount = Math.max(0, cap - Math.max(0, spent));
  }

  const leftCents = allowanceCents(leftAmount);
  const limitCents = hasCap ? Math.max(0, Math.round(cap * 100 + 1e-6)) : null;

  let usedCents = 0;
  if (limitCents != null) {
    const derivedCents = Math.max(0, limitCents - leftCents);
    const usageAgrees = remaining != null
      && spent != null
      && Math.abs((spent + remaining) - cap) <= USAGE_AGREES;
    if (usageAgrees) {
      const usageCents = Math.max(0, Math.round(spent * 100));
      usedCents = usageCents + leftCents === limitCents ? usageCents : derivedCents;
    } else {
      usedCents = derivedCents;
    }
  } else if (spent != null && spent > 0) {
    usedCents = allowanceCents(spent);
  }

  return { leftCents, usedCents, limitCents };
}

export function usedOfLimitLabel(budget) {
  if (!budget || budget.limitCents == null) return null;
  return `Used: ${formatCents(budget.usedCents)} of ${formatCents(budget.limitCents)}`;
}

/**
 * A payload that actually says whether a key or balance is present.
 * A failed read is an empty or missing payload.
 */
export function settingsIdentifyKey(settings) {
  if (!settings || typeof settings !== 'object') return false;
  return (
    Object.prototype.hasOwnProperty.call(settings, 'has_api_key')
    || Object.prototype.hasOwnProperty.call(settings, 'api_key_valid')
    || Object.prototype.hasOwnProperty.call(settings, 'key_provisioning')
    || Object.prototype.hasOwnProperty.call(settings, 'credits_remaining')
  );
}

/** Unknown reads stay ready. A known missing key does not. */
export function spendAllowed(settings) {
  if (!settingsIdentifyKey(settings)) return true;
  return chatSpendPossible(settings);
}

/**
 * What the composer shows when the writer cannot spend yet.
 * A pending or blocked beta allowance never offers the API key screen.
 * A failed read is not a missing key.
 */
export function composerBlockedCopy(settings, { settingsLoaded = true } = {}) {
  // Settings still loading is not a key being created. A ready account
  // must not see "Getting your account ready…" after a refresh.
  if (!settingsLoaded) return null;
  if (!settingsIdentifyKey(settings)) return null;
  if (chatSpendPossible(settings)) return null;
  if (settings?.key_provisioning === 'pending') {
    return { message: ACCOUNT_READY_MESSAGE, showConfigure: false };
  }
  if (settings?.key_provisioning === 'blocked') {
    return { message: BETA_BLOCKED_MESSAGE, showConfigure: false };
  }
  if (settings?.key_provisioning === 'own_needed') {
    return { message: BETA_ALLOWANCE_USED_MESSAGE, showConfigure: true };
  }
  return { message: CONFIGURE_KEY_MESSAGE, showConfigure: true };
}

/**
 * Balance-dialog copy. Quiet above $0.50. Almost used up under that.
 * Zero cents is the pause.
 */
export function allowanceSwitchCopy(balance) {
  const amount = Number(balance);
  if (!Number.isFinite(amount) || amount >= OWN_KEY_SWITCH_USD) {
    return { message: BETA_ALLOWANCE_QUIET_LINE, offerKey: false };
  }
  if (allowanceCents(amount) === 0) {
    return { message: BETA_ALLOWANCE_USED_MESSAGE, offerKey: true };
  }
  return { message: BETA_ALLOWANCE_ALMOST_MESSAGE, offerKey: true };
}

export function shouldPollKeyProvisioning(settings) {
  return settings?.key_provisioning === 'pending';
}

export function creditsErrorCopy(err) {
  const message = String(err?.message || '');
  const provisioned = Boolean(err?.provisioned)
    || Boolean(err?.detail?.provisioned)
    || message.toLowerCase().includes('beta allowance');
  if (provisioned) {
    return {
      message: BETA_ALLOWANCE_USED_MESSAGE,
      type: 'allowance_used',
    };
  }
  return {
    message: 'Your OpenRouter account has insufficient credits. Please add credits to continue.',
    type: 'insufficient_credits',
  };
}
